import * as React from "react";
import {
  IXGUIFieldBase,
  TOnChange,
  TXGUIField,
} from "../../../utils/interfaces/gui/fields.interfaces";
import { UNITS } from "../../../utils/constants/gui/units";
import { SIDES_NAME } from "../../../utils/constants/gui/sidesName";
import { TRadius } from "../../../utils/types/gui/field.types";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

const compareProps = (prevProps: any, nextProps: any) => {
  // return isEqual(prevProps, nextProps);
  return prevProps.currentValue === nextProps.currentValue;
};

interface XGUIRadiusFieldProps extends IXGUIFieldBase {
  onChange: TOnChange<TRadius | undefined>;
  currentValue: TRadius;
}

const XGUIRadiusField: React.FC<XGUIRadiusFieldProps> = React.memo(
  ({ field, currentValue, onChange }) => {
    const value = currentValue ?? field.defaultValue ?? {};
    const bp = useBreakpoint();

    const setValue = (_value: TRadius) => {
      onChange(_value, field.name, bp);
    };

    const id = `XGUI-FIELD-${field.name}`;

    const section = (
      <React.Fragment>
        <Typography component="label" htmlFor={id} textAlign="center">
          {field.label}
        </Typography>
        <Stack spacing={1} alignItems="center" mt={1}>
          <Stack spacing={1} justifyContent="space-between" direction="row">
            <Field
              field={field}
              id={id}
              side="TopLeft"
              value={value}
              setValue={setValue}
            />{" "}
            <Field
              field={field}
              id={id}
              side="TopRight"
              value={value}
              setValue={setValue}
            />
          </Stack>
          <Stack spacing={1} justifyContent="space-between" direction="row">
            <Field
              field={field}
              id={id}
              side="BottomLeft"
              value={value}
              setValue={setValue}
            />
            <Field
              field={field}
              id={id}
              side="BottomRight"
              value={value}
              setValue={setValue}
            />
          </Stack>
        </Stack>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />
      </React.Fragment>
    );

    return section;
  },
  compareProps
);

export default XGUIRadiusField;

interface FieldProps {
  id: string;
  side: "TopLeft" | "TopRight" | "BottomRight" | "BottomLeft";
  value: TRadius;
  setValue: (newValue: TRadius) => void;
  field: TXGUIField;
}

const Field: React.FC<FieldProps> = ({ id, side, value, field, setValue }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sideValue = value[side];

  const [unit, setUnit] = React.useState<string>(
    sideValue ? (sideValue ?? "").replace(/[0-9]/g, "") : "px"
  );

  React.useEffect(() => {
    if (sideValue) {
      if (!sideValue.includes(unit)) {
        const _unit = sideValue.replace(/[0-9]/gi, "");

        if (_unit.length > 0) setUnit(_unit);
      }
    }
  }, [sideValue]);

  return (
    <Stack
      // style={{ flex: 1, maxWidth: 120 }}
      className="XGUIFieldOption"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <TextField
        id={id + "-" + side}
        fullWidth
        hiddenLabel
        size="small"
        type="number"
        placeholder={SIDES_NAME[side]}
        value={sideValue && parseInt(sideValue.replace(/[^0-9]/gi, "") ?? "0")}
        onChange={(e) => {
          const _val = e.target.value + unit;
          setValue({ ...value, [side]: _val });
        }}
        InputProps={{
          endAdornment: (
            <>
              <Button
                id={`XGUIFieldOption-Btn-${name}`}
                aria-controls={
                  open ? `XGUIFieldOption-Menu-${name}` : undefined
                }
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                color="inherit"
              >
                {unit}
              </Button>
              <Menu
                id={`XGUIFieldOption-Menu-${name}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": `XGUIFieldOption-Btn-${name}`,
                }}
                sx={{
                  ".MuiList-root": {
                    p: 0,

                    ".MuiButtonBase-root": {
                      fontSize: 10,
                      px: 1,
                      fontWeight: 600,
                      justifyContent: "center",
                    },
                  },
                }}
              >
                {(field.units ?? UNITS).map((_unit) => {
                  return (
                    <MenuItem
                      key={_unit}
                      onClick={() => {
                        setUnit((oldUnit) => {
                          const _value =
                            (sideValue ?? "0").replace(oldUnit, "") + _unit;

                          if (sideValue || _unit === "auto")
                            setValue({ ...value, [side]: _value });

                          return _unit;
                        });

                        handleClose();
                      }}
                    >
                      {_unit}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ),
        }}
      />
    </Stack>
  );
};

// const Field: React.FC<FieldProps> = ({ id, side, value, field, setValue }) => {
//   const sideValue = value[side];

//   const [unit, setUnit] = React.useState<string>("px");

//   React.useEffect(() => {
//     if (sideValue) {
//       if (!sideValue.includes(unit)) {
//         const _unit = sideValue.replace(/[0-9]/gi, "");

//         if (_unit.length > 0) setUnit(_unit);
//       }
//     }
//   }, [sideValue]);

//   return (
//     <XDiv className="XGUI-Field-Control">
//       <input
//         id={id + "-" + side}
//         className="XGUI-Field-Input With-Addon-Right"
//         placeholder={SIDES_NAME[side]}
//         value={sideValue && parseInt(sideValue.replace(/[^0-9]/gi, "") ?? "0")}
//         onChange={(e) => {
//           const _val = e.target.value + unit;
//           setValue({ ...value, [side]: _val });
//         }}
//         type="number"
//         style={{ width: "100%", paddingRight: 0 }}
//       />
//       <select
//         style={{ width: 65 }}
//         id={id}
//         className="XGUI-Field-Select Addon-Right"
//         value={unit}
//         onChange={(e) => {
//           setUnit((oldUnit) => {
//             const newUnit = e.target.value;

//             const _value = (sideValue ?? "0").replace(oldUnit, "") + newUnit;

//             if (sideValue || newUnit === "auto")
//               setValue({ ...value, [side]: _value });

//             return newUnit;
//           });
//         }}
//       >
//         {(field.units ?? UNITS).map((opt) => (
//           <option key={opt} value={opt}>
//             {opt}
//           </option>
//         ))}
//       </select>
//     </XDiv>
//   );
// };
